'use client'
import {
  FormControl,
  FormDescription,
  FormItem,
  FormMessage,
  useFormField,
} from '@/components/ui/form/form'
import { Input, Label } from '@/components/ui/input/input'
import { FormMode } from '@/lib/common-models'
import { ControllerRenderProps, Path } from 'react-hook-form'
import { TypeOf, ZodSchema } from 'zod'
import { Button } from '@/components/ui/button/button'
import { cn } from '@/lib/utils'
import { useState, useRef } from 'react'
import EyeIcon from '@/public/image/icons/eye-icon.svg'
import Image from 'next/image'

interface PasswordInputProps<T extends ZodSchema, K extends Path<TypeOf<T>>> {
  field: ControllerRenderProps<TypeOf<T>, K>
  mode: FormMode
  label: string
  /**
   * Transaction name for RUM monitoring.
   * Required for observability - tracks user interactions in Elastic APM.
   * Format: area.page.field_name (e.g., "admin.login.password")
   */
  transactionName: string
  readOnly?: boolean
  description?: string
  uppercase?: boolean
}

const getInputClassName = (
  uppercase?: boolean,
  error?: boolean,
  readOnly?: boolean
): string =>
  cn(
    'pr-12 md:w-full text-black font-semibold',
    uppercase && 'uppercase',
    error &&
      'focus-within:border-red-500 focus:ring-2 focus-visible:ring-red-500',
    readOnly && 'text-gray-400'
  )

const getLabelClassName = (error?: boolean, readOnly?: boolean): string =>
  cn(
    'absolute left-3 top-4 p-1',
    error &&
      'border-red-500 focus-within:border-red-500 focus:border-red-500 focus:ring-2 focus-visible:border-red-500 focus-visible:ring-red-500 peer-focus:text-red-500',
    readOnly && 'text-gray-400'
  )

export const PasswordInput = <T extends ZodSchema, K extends Path<TypeOf<T>>>({
  field,
  mode,
  label,
  transactionName,
  readOnly,
  description,
  uppercase,
}: PasswordInputProps<T, K>) => {
  const { error } = useFormField()
  const [showPassword, setShowPassword] = useState(false)
  const inputRef = useRef<HTMLInputElement | null>(null)
  const hasError = Boolean(error)
  const handlePaste = (e: { preventDefault: () => void }) => {
    e.preventDefault()
  }

  if (mode !== FormMode.CREATE) {
    return <></>
  }

  const handleTogglePassword = () => {
    const input = inputRef.current
    if (!input) return
    const start = input.selectionStart
    const end = input.selectionEnd
    setShowPassword((prev) => !prev)
    requestAnimationFrame(() => {
      if (start !== null && end !== null) {
        input.setSelectionRange(start, end)
      }
    })
  }

  return (
    <FormItem className="animate-fade-in">
      <div className="group relative w-full md:w-[21.8125rem]">
        <FormControl>
          <Input
            autoComplete="off"
            type={showPassword ? 'text' : 'password'}
            className={getInputClassName(uppercase, hasError, readOnly)}
            readOnly={readOnly}
            id={field.name}
            {...field}
            ref={(el) => {
              inputRef.current = el
              field.ref(el)
            }}
            onPaste={handlePaste}
            draggable="false"
            onDrop={handlePaste}
            transactionName={transactionName}
          />
        </FormControl>
        <Label
          className={getLabelClassName(hasError, readOnly)}
          htmlFor={field.name}
        >
          {label}
        </Label>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          disabled={readOnly}
          aria-label={showPassword ? 'Hide password' : 'Show password'}
          onMouseDown={(e) => e.preventDefault()}
          onClick={handleTogglePassword}
          className="pointer-events-none absolute right-4 top-1/2 size-auto -translate-y-1/2 p-0 text-black opacity-0 transition-opacity hover:bg-transparent group-focus-within:pointer-events-auto group-focus-within:opacity-100"
          transactionName="component.ui.passwordinput"
        >
          <Image src={EyeIcon} alt="eye-icon" height={20} width={20} />
        </Button>
      </div>
      <FormMessage />
      {!error?.message?.toString() && (
        <FormDescription>{description}</FormDescription>
      )}
    </FormItem>
  )
}
